/* eslint-disable @typescript-eslint/no-unused-vars */
import { Note, UUID, Profile, TypeOfNote, DocumentSection, RelatedResult } from '@annote/types';
import { IDatabase, DatabaseMessage, AuthToken } from './types';
import { TokenProvider, performAuthenticatedFetch } from '../utils/authenticatedFetch';

export class AppMessageDatabase implements IDatabase, TokenProvider {
  private messageId = 0;

  profile?: Profile;

  constructor() {
    console.log('AppMessageDb: Initializing');
  }

  private sendMessage = <T>(message: DatabaseMessage): Promise<T> => {
    return new Promise((resolve, reject) => {
      this.messageId += 1;
      const { messageId } = this;

      const listener = (event: MessageEvent): void => {
        if (event.source !== window) return;
        if (event.data.messageType !== 'EXT_RESPONSE') return;
        if (event.data.messageId === messageId) {
          window.removeEventListener('message', listener);
          if (event.data.error) {
            console.log('AppMessageDb - received error', event.data.error, event.data);
            reject(new Error(event.data.error));
          } else {
            resolve(event.data.result);
          }
        }
      };

      window.addEventListener('message', listener);
      window.postMessage({ messageId, messageType: 'APP_MESSAGE', ...message }, '*');
    });
  };

  // TokenProvider interface implementation
  getAuthToken = async (): Promise<AuthToken> => {
    return this.sendMessage<AuthToken>({ action: 'getAuthToken' });
  };

  getNotes = async (ids?: UUID[]): Promise<Note[]> => {
    return this.sendMessage<Note[]>({ action: 'getNotes', ids });
  };

  getNotesByUrl = async (url: string): Promise<{ notes: Note[]; isBlocked: boolean }> => {
    return this.sendMessage<{ notes: Note[]; isBlocked: boolean }>({ action: 'getNotesByUrl', url });
  };

  getNoteById = async (id: UUID): Promise<Note | null> => {
    return this.sendMessage<Note | null>({ action: 'getNoteById', id });
  };

  getNoteByTitle = async (title: string): Promise<Note | null> => {
    return this.sendMessage<Note | null>({ action: 'getNoteByTitle', title });
  };

  searchNotes = async (query: string, types?: TypeOfNote[], limit: number = 10): Promise<Note[]> => {
    return this.sendMessage<Note[]>({ action: 'searchNotes', query, types, limit });
  };

  updateNotes = async (notes: Note[]): Promise<void> => {
    return this.sendMessage<void>({ action: 'updateNotes', notes });
  };

  deleteNotes = async (notes: Note[]): Promise<void> => {
    if (!notes || !Array.isArray(notes) || notes.length === 0) {
      throw new Error('Invalid notes array provided for deletion');
    }
    return this.sendMessage<void>({ action: 'deleteNotes', notes });
  };

  getProfile = async (forceRefresh: boolean = false): Promise<Profile> => {
    return this.sendMessage<Profile>({ action: 'getProfile', forceRefresh });
  };

  updateProfile = async (profile: Profile): Promise<void> => {
    return this.sendMessage<void>({ action: 'updateProfile', profile });
  };

  isAuthenticated = async (): Promise<boolean> => {
    return this.sendMessage<boolean>({ action: 'isAuthenticated' });
  };

  // Forwards a Google Identity credential to the extension so it can establish
  // its own Supabase session, then returns the freshly-fetched profile.
  signInWithGoogleCredential = async (credential: string): Promise<Profile | null> => {
    const profile = await this.sendMessage<Profile | null>({ action: 'signInWithGoogleCredential', credential });
    this.profile = profile ?? undefined;
    return profile;
  };

  findRelatedNotes = async (
    ids: UUID[],
    limit: number = 10,
    similarityThreshold: number = 0.7,
  ): Promise<RelatedResult[]> => {
    return this.sendMessage<RelatedResult[]>({
      action: 'findRelatedNotes',
      ids,
      limit,
      similarityThreshold,
    });
  };

  fetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    console.log('AppMessageDb - fetch!', input, init);
    return performAuthenticatedFetch(this, input, init);
  };

  getAnnoteIndex = async (): Promise<Record<UUID, { links: UUID[]; backlinks: UUID[] }>> => {
    return this.sendMessage<Record<UUID, { links: UUID[]; backlinks: UUID[] }>>({ action: 'getAnnoteIndex' });
  };
}

export default AppMessageDatabase;
